/**
 * ADMIN MODULE
 * Internal ops layer — platform_admins only.
 * Never import this from any product module.
 *
 * The module launch catalog. `modules.is_live` answers "have we finished
 * building this?" — a Brand Command staff decision, separate from whether a
 * given tenant's plan includes it (that's subscriptions.entitled_modules,
 * enforced in RLS by account_has_module). A module needs BOTH.
 *
 * Writes are staff-only at the DB level (migration 0066 policy
 * `modules_write_staff` → is_platform_admin()), so this service is convenience,
 * not the gate.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";

export const CATEGORIES = [
  { key: "sales",      label: "Sales" },
  { key: "marketing",  label: "Marketing" },
  { key: "operations", label: "Operations" },
];

const SELECT =
  "module_id, name, description, is_live, route, category, sort_order, went_live_at, beta_account_ids";

/** The whole catalog, ordered the way the launcher orders it. */
export async function listModules() {
  const { data, error } = await supabase
    .from("modules")
    .select(SELECT)
    .order("sort_order", { ascending: true });
  if (error) {
    console.error("[moduleCatalogService.listModules]", error);
    throw new Error("Could not load the module catalog.");
  }
  return data ?? [];
}

/**
 * Flip a module live or dark. Stamps went_live_at the first time it goes live
 * and leaves it alone afterwards, so the field records the original launch
 * date rather than the last toggle.
 */
export async function setModuleLive(moduleId, isLive, alreadyStamped) {
  const patch = { is_live: !!isLive };
  if (isLive && !alreadyStamped) patch.went_live_at = new Date().toISOString();

  const { data, error } = await supabase
    .from("modules")
    .update(patch)
    .eq("module_id", moduleId)
    .select(SELECT)
    .single();
  if (error) {
    console.error("[moduleCatalogService.setModuleLive]", error);
    throw new Error("Could not change the module's status.");
  }
  return data;
}

/** Move a module between launcher columns. */
export async function setModuleCategory(moduleId, category) {
  const valid = CATEGORIES.some((c) => c.key === category);
  if (category !== null && !valid) throw new Error("Unknown category.");

  const { data, error } = await supabase
    .from("modules")
    .update({ category })
    .eq("module_id", moduleId)
    .select(SELECT)
    .single();
  if (error) {
    console.error("[moduleCatalogService.setModuleCategory]", error);
    throw new Error("Could not move the module.");
  }
  return data;
}
